import { useCallback, useMemo, useState } from "react";

import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  MAX_FILES_PER_JOB,
  MAX_ZIP_SIZE_BYTES,
  MAX_ZIP_SIZE_MB,
} from "./constants";
import type { FileEntry, ValidationError } from "./FileItem";

let entryCounter = 0;
function nextKey(): string {
  entryCounter += 1;
  return `f-${Date.now()}-${entryCounter}`;
}

/**
 * 파일 1건 검증.
 *
 * 사용자 친화 메시지를 직접 반환하지 않고 ``ValidationError`` (code +
 * 파라미터) 만 반환한다. 메시지화는 렌더 시점 (``FileItem``) 의
 * ``t()`` 책임 — 언어 변경 시 stored 메시지가 stale 되는 문제 방지.
 */
function validate(file: File): ValidationError | null {
  const name = file.name.toLowerCase();
  // M25 — ZIP(의뢰) 업로드 허용. PDF 보다 큰 상한 적용.
  if (name.endsWith(".zip")) {
    if (file.size === 0) return { code: "empty" };
    if (file.size > MAX_ZIP_SIZE_BYTES) {
      return { code: "too_large", maxMb: MAX_ZIP_SIZE_MB };
    }
    return null;
  }
  if (!name.endsWith(".pdf")) return { code: "not_pdf" };
  if (file.size === 0) return { code: "empty" };
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { code: "too_large", maxMb: MAX_FILE_SIZE_MB };
  }
  return null;
}

interface UseUploadFiles {
  entries: FileEntry[];
  validFiles: File[];
  hasInvalid: boolean;
  /** 파일 N개 추가 — 동일 이름+크기 조합은 중복 제거. */
  addFiles: (files: File[]) => void;
  removeFile: (key: string) => void;
  reset: () => void;
}

export function useUploadFiles(): UseUploadFiles {
  const [entries, setEntries] = useState<FileEntry[]>([]);

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;
    setEntries((prev) => {
      const seen = new Set(prev.map((e) => `${e.file.name}__${e.file.size}`));
      const next: FileEntry[] = [...prev];
      for (const file of files) {
        if (next.length >= MAX_FILES_PER_JOB) break;
        const sig = `${file.name}__${file.size}`;
        if (seen.has(sig)) continue;
        seen.add(sig);
        next.push({ key: nextKey(), file, error: validate(file) });
      }
      return next;
    });
  }, []);

  const removeFile = useCallback((key: string) => {
    setEntries((prev) => prev.filter((e) => e.key !== key));
  }, []);

  const reset = useCallback(() => setEntries([]), []);

  const validFiles = useMemo(
    () => entries.filter((e) => e.error === null).map((e) => e.file),
    [entries],
  );
  const hasInvalid = useMemo(() => entries.some((e) => e.error !== null), [entries]);

  return { entries, validFiles, hasInvalid, addFiles, removeFile, reset };
}
